import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import levelstairLogo from "../../assets/images/levelstair-logo.png";

const FooterPremium = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: "Discord",
      icon: "logo-discord",
      url: "https://discord.com/invite/xauShxdx6u",
      color: "from-blue-500 to-indigo-600",
    },
    {
      name: "YouTube",
      icon: "logo-youtube",
      url: "https://www.youtube.com/@LIVNEU_Levelstair",
      color: "from-red-500 to-red-600",
    },
    {
      name: "Twitter",
      icon: "logo-twitter",
      url: "https://x.com/levelstair",
      color: "from-blue-400 to-blue-500",
    },
    {
      name: "Instagram",
      icon: "logo-instagram",
      url: "https://www.instagram.com/levelstair/",
      color: "from-pink-500 to-purple-600",
    },
    {
      name: "Facebook",
      icon: "logo-facebook",
      url: "https://www.facebook.com/LEVELSTAIR/",
      color: "from-blue-600 to-blue-700",
    },
    {
      name: "LinkedIn",
      icon: "logo-linkedin",
      url: "https://www.linkedin.com/company/project-livneu",
      color: "from-blue-700 to-blue-800",
    },
    {
      name: "Reddit",
      icon: "logo-reddit",
      url: "https://www.reddit.com/r/LEVELSTAIR/",
      color: "from-blue-500 to-red-500",
    },
    {
      name: "WhatsApp",
      icon: "logo-whatsapp",
      url: "https://chat.whatsapp.com/HlqjZtbFtU6ISAEMYBxVRz",
      color: "from-green-500 to-emerald-600",
    },
  ];

  const footerLinks = [
    {
      title: "Agency",
      links: [
        { name: "About", path: "/about" },
        { name: "Auditions", path: "/auditions" },
        { name: "Apply Now", path: "/apply" },
        { name: "FAQ", path: "/faq" },
      ],
    },
    {
      title: "Support",
      links: [
        { name: "Contact", path: "/contact" },
        { name: "FAQ", path: "/faq" },
        { name: "Community Guidelines", path: "/derivative-works" },
      ],
    },
    {
      title: "Legal",
      links: [
        { name: "Privacy Policy", path: "/privacy-policy" },
        { name: "Derivative Works", path: "/derivative-works" },
        { name: "Request to Minors", path: "/request-to-minors" },
        { name: "Terms of Service", path: "/terms" },
      ],
    },
  ];

  return (
    <footer className="bg-gradient-footer text-black relative overflow-hidden">
      {/* Kawaii floating elements background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-pastel-purple rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 right-1/3 w-20 h-20 bg-pastel-blue rounded-full animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/3 w-24 h-24 bg-pastel-mint rounded-full animate-pulse"></div>
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-12">
            {/* Brand Section */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h3 className="text-2xl font-title mb-4 text-pastel-purple kawaii-glow">
                  Livneu
                </h3>
                <p className="text-gray-700 mb-6 leading-relaxed font-body">
                  Sri Lanka's premier VTuber agency, empowering the next
                  generation of virtual stars through professional management
                  and cutting-edge technology.
                </p>

                {/* Powered by LEVELSTAIR */}
                <div className="flex items-center gap-3 p-4 bg-card-background/20 backdrop-blur-md rounded-2xl border border-pastel-purple/30 shadow-kawaii">
                  <img
                    src={levelstairLogo}
                    alt="LEVELSTAIR"
                    className="h-8 w-auto"
                  />
                  <div>
                    <p className="text-sm text-gray-400 font-accent">
                      Powered by
                    </p>
                    <p className="text-black font-accent font-medium">
                      LEVELSTAIR
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Footer Links */}
            {footerLinks.map((section, index) => (
              <div key={section.title}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <h4 className="text-lg font-accent font-medium mb-6 text-black">
                    {section.title}
                  </h4>
                  <ul className="space-y-3">
                    {section.links.map((link) => (
                      <li key={link.name}>
                        <Link
                          to={link.path}
                          className="text-gray-700 hover:text-pastel-purple transition-colors duration-300 font-body"
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            ))}
          </div>
        </div>

        {/* Social Media Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="py-8 border-t border-pastel-purple/20"
        >
          <div className="text-center mb-8">
            <h4 className="text-xl font-accent font-medium text-black mb-4">
              Follow Our Journey
            </h4>
            <p className="text-gray-700 mb-6 font-body">
              Stay connected with the latest updates, behind-the-scenes content,
              and community events
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {socialLinks.map((social, index) => (
              <motion.a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-12 h-12 bg-gradient-to-br ${social.color} rounded-xl flex items-center justify-center hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl`}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <ion-icon
                  name={social.icon}
                  style={{ fontSize: "1.5rem", color: "white" }}
                ></ion-icon>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center md:text-left"
            >
              <p
                className="text-gray-400 text-sm"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                © {currentYear} Livneu. All rights reserved.
              </p>
              <p className="text-pastel-purple font-accent font-medium text-sm mt-1">
                "The Digital Stardom of LK"
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="flex flex-wrap gap-6 text-sm"
            >
              <Link
                to="/privacy-policy"
                className="text-gray-400 hover:text-pastel-purple transition-colors font-body"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-gray-400 hover:text-pastel-purple transition-colors font-body"
              >
                Terms of Service
              </Link>
              <Link
                to="/conduct"
                className="text-gray-400 hover:text-pastel-purple transition-colors font-body"
              >
                Code of Conduct
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Fun Easter Egg */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="text-center py-4"
        >
          <p
            className="text-gray-500 text-xs"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Made with ❤️ by the Livneu team • Building Sri Lanka's First
            Vtuber Agency
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default FooterPremium;
